import { useState, type FormEvent } from 'react';
import { useWheelSpins } from '@/api/hooks';
import { WHEEL_SPIN_STATUSES, WHEEL_TIERS, type WheelSpin, type WheelSpinsFilters } from '@/api/types';
import { useAgent } from '@/auth/use-agent';
import { formatDateTime, formatDay } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Field, INPUT_CLASS } from '@/ui/Field';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { Table, Td, Th } from '@/ui/Table';

const EMPTY: WheelSpinsFilters = { playerUuid: '', tier: '', status: '', from: '', to: '' };

// Accepts the PLAYER- form agents copy from the backoffice as well as the bare uuid the
// ledger stores.
const toUuid = (value: string): string =>
  value
    .trim()
    .replace(/^PLAYER-/i, '')
    .toLowerCase();

const STATUS_HELP: Record<WheelSpin['status'], string> = {
  granted: 'Prize credited to the player.',
  failed: 'Draw made, credit failed — check the Slack alert and credit by hand.',
  pending_choice: 'Won while holding a deposit bonus; the player has not chosen replace or keep yet.',
  declined: 'The player kept their current bonus instead of this prize.',
  drawn: 'Draw recorded but the outcome was never settled — investigate.',
};

function formatDeposit(spin: WheelSpin): string {
  if (spin.depositTotal === null) return '–';
  return `${spin.depositTotal} ${spin.depositCurrency ?? ''}`.trim();
}

function SpinsTable({ spins }: { spins: WheelSpin[] }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th align="left">Spun at</Th>
          <Th align="left">Day (UTC)</Th>
          <Th align="left">Player</Th>
          <Th align="left">Tier</Th>
          <Th align="left">Wheel</Th>
          <Th align="left">Prize</Th>
          <Th align="left">Status</Th>
          <Th>Deposited that day</Th>
          <Th align="left">Campaign</Th>
          <Th align="left">Settled</Th>
        </tr>
      </thead>
      <tbody>
        {spins.length === 0 && (
          <tr>
            <Td align="left" colSpan={10} className="text-slate-500">
              No spins match these filters.
            </Td>
          </tr>
        )}
        {spins.map((spin) => (
          <tr key={spin.id}>
            <Td align="left">{formatDateTime(spin.spunAt)}</Td>
            <Td align="left" title="The UTC calendar day the spin counted for; the From/To filters use it">
              {formatDay(spin.day)}
            </Td>
            <Td align="left" className="font-mono text-xs">
              {toPlayerId(spin.playerUuid)}
            </Td>
            <Td align="left">
              <Badge value={spin.tier} />
            </Td>
            <Td align="left">
              {spin.wheelUid}
              {spin.wheelTier !== spin.tier && (
                <span
                  className="ml-2 text-xs text-slate-500"
                  title="No wheel for the entitled tier; the next tier down was served"
                >
                  served {spin.wheelTier}
                </span>
              )}
            </Td>
            <Td align="left">
              {spin.segmentLabel ?? spin.segmentUid}
              {spin.segmentLabel && <span className="ml-2 font-mono text-xs text-slate-400">{spin.segmentUid}</span>}
            </Td>
            <Td align="left" title={STATUS_HELP[spin.status]}>
              <Badge value={spin.status} />
            </Td>
            <Td>{formatDeposit(spin)}</Td>
            <Td align="left" className="font-mono text-xs">
              {spin.campaignUuid ?? '–'}
              {spin.replacedCampaignUuid && (
                <span className="block text-slate-400">replaced {spin.replacedCampaignUuid}</span>
              )}
            </Td>
            <Td align="left">{formatDateTime(spin.settledAt)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Filters({ onApply }: { onApply(filters: WheelSpinsFilters): void }) {
  const [draft, setDraft] = useState<WheelSpinsFilters>(EMPTY);
  const set = (name: keyof WheelSpinsFilters) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [name]: event.target.value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply({ ...draft, playerUuid: toUuid(draft.playerUuid ?? '') });
  };

  const reset = () => {
    setDraft(EMPTY);
    onApply(EMPTY);
  };

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-6" aria-label="Filters">
      <div className="md:col-span-2">
        <Field id="wheel-player" label="Player ID" hint="PLAYER-… or the bare uuid; leave empty for everyone.">
          <input id="wheel-player" className={INPUT_CLASS} value={draft.playerUuid} onChange={set('playerUuid')} />
        </Field>
      </div>
      <Field id="wheel-tier" label="Tier">
        <select id="wheel-tier" className={INPUT_CLASS} value={draft.tier} onChange={set('tier')}>
          <option value="">Any</option>
          {WHEEL_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {tier}
            </option>
          ))}
        </select>
      </Field>
      <Field id="wheel-status" label="Status">
        <select id="wheel-status" className={INPUT_CLASS} value={draft.status} onChange={set('status')}>
          <option value="">Any</option>
          {WHEEL_SPIN_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status.replace('_', ' ')}
            </option>
          ))}
        </select>
      </Field>
      <Field id="wheel-from" label="From (UTC day)">
        <input id="wheel-from" type="date" className={INPUT_CLASS} value={draft.from} onChange={set('from')} />
      </Field>
      <Field id="wheel-to" label="To (UTC day)">
        <input id="wheel-to" type="date" className={INPUT_CLASS} value={draft.to} onChange={set('to')} />
      </Field>
      <div className="flex items-end gap-2 md:col-span-6">
        <Button type="submit">Apply</Button>
        <Button type="button" variant="secondary" onClick={reset}>
          Clear
        </Button>
      </div>
    </form>
  );
}

export function WheelSpinsPage() {
  const { agent } = useAgent();
  const [filters, setFilters] = useState<WheelSpinsFilters>(EMPTY);
  // Hooks run before the admin check; the query itself stays off for an agent.
  const query = useWheelSpins(filters, agent.admin);

  if (!agent.admin) {
    return <Notice>The daily wheel history is for admins only.</Notice>;
  }

  const spins = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Daily wheel spins</h1>
        <p className="text-sm text-slate-600">
          Every spin recorded in the ledger, newest first. One spin per qualifying deposit; each tier once per UTC day.
        </p>
      </div>

      <Card title="Filters">
        <Filters onApply={setFilters} />
      </Card>

      <Card title="Spins" description={query.data ? `${spins.length} loaded` : undefined}>
        {query.isPending && <Loading what="spins" />}
        {query.isError && <ErrorNotice error={query.error} />}
        {query.data && (
          <>
            <SpinsTable spins={spins} />
            {query.hasNextPage && (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  onClick={() => void query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage ? 'Loading…' : 'Load older spins'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
